import type { ReactNode } from 'react'

/**
 * Capul de pagina si banda de file ale catalogului de servicii.
 *
 * Doua bucati, nu doua copii pe fiecare ecran: catalogul are patru pagini
 * (lista, compunerea, dependentele, tabloul de costuri) si toate patru poarta
 * acelasi cap si aceeasi banda. Scrise separat, s-ar fi despartit la prima
 * corectura facuta intr-una singura.
 */

/**
 * `version-3/softsee/adm/_pagehead`.
 *
 * Aceleasi clase ca in portal (`adm-page-head`, `adm-tone-*`, `adm-stat-*`),
 * pentru ca foaia care le deseneaza e chiar `public/css/adm.css` a portalului.
 * `value` e text, nu numar: pe tabloul de costuri cifra din stanga e o suma cu
 * moneda ei, iar un `number` ar fi cerut o a doua bucata la fel.
 */
export function CatalogHead({ title, icon, tone, note, actions, stats }: {
  title: string
  icon: string
  tone: string
  note: string
  actions?: ReactNode
  stats: { value: ReactNode; label: string; tone: string }[]
}) {
  return (
    <div className={`adm-page-head adm-tone-${tone}`}>
      <span className="adm-page-icon">
        <i className={`fas ${icon}`}></i>
      </span>
      <div className="adm-page-title">
        <div className="adm-page-title-line">
          <h3>{title}</h3>
          {actions && <span className="adm-page-actions">{actions}</span>}
        </div>
        <span className="adm-page-note">{note}</span>
      </div>
      <div className="adm-page-stats">
        {stats.map((one) => (
          <div className={`adm-stat adm-stat-${one.tone}`} key={one.label}>
            <span className="adm-stat-value">{one.value}</span>
            <span className="adm-stat-label">{one.label}</span>
          </div>
        ))}
      </div>
    </div>
  )
}

/**
 * `views/version-3/easeedesk/service_shop/_tabs.php`, partea de administrare.
 *
 * Filele vin DUPA titlu, ca in magazin: titlul mare spune unde esti, filele
 * spun unde poti trece de aici.
 *
 * COSUL NU SE DESENEAZA AICI, si nici in portal: pe ecranele de administrare nu
 * se cere nimic, iar un cos care nu se deschide e mai rau decat unul care
 * lipseste. Fila "Service Catalog" apare numai pe ecranele care ATARNA de
 * dreptul catalogului - aici suntem deja inauntrul lor, deci se deseneaza
 * mereu; in magazin o pune `ShopTabs`, care intreaba intai serverul.
 *
 * Adresele sunt ale PORTALULUI, mutate sub `/app`: `/app/service_catalog`,
 * `/app/service_shop`, `/app/service_shop/requests`. In portal cele trei
 * controllere stau la radacina, si o legatura care mergea ieri trebuie sa
 * mearga si maine.
 */
export function CatalogTabs({ active }: { active: 'catalog' | 'shop' | 'requests' }) {
  return (
    <div className="shop-tabs">
      <a className={`shop-tab${active === 'catalog' ? ' is-on' : ''}`} href="/app/service_catalog">
        <i className="fas fa-sitemap"></i> Service Catalog
      </a>

      <a className={`shop-tab${active === 'shop' ? ' is-on' : ''}`} href="/app/service_shop">
        <i className="fas fa-store"></i> Service Catalog Shop
      </a>

      <a className={`shop-tab${active === 'requests' ? ' is-on' : ''}`} href="/app/service_shop/requests">
        <i className="fas fa-clipboard-list"></i> My requests
      </a>

      <span style={{ flex: 1 }}></span>
    </div>
  )
}

/**
 * "Cine a spus-o" - `$origins` din `composition.php` si `dependencies.php`.
 *
 * Aceleasi trei cuvinte pe amandoua ecranele, fiindca sunt acelasi fel de rand
 * din `it_ci_relations`.
 */
export function OriginPill({ origin }: { origin: number }) {
  const words: Record<number, { label: string; tone: string }> = {
    1: { label: 'declared', tone: 'adm-pill-ok' },
    2: { label: 'discovered', tone: 'adm-pill-running' },
    3: { label: 'by rule', tone: 'adm-pill' },
  }
  const one = words[origin] ?? words[2]

  return <span className={`adm-pill ${one.tone}`}>{one.label}</span>
}

/** Felul serviciului, ca in lista: business intai, IT dupa. */
export function KindPill({ kind }: { kind: number }) {
  return (
    <span className={`adm-pill${kind === 4 ? ' adm-pill-running' : ''}`}>{kind === 4 ? 'Business' : 'IT'}</span>
  )
}
